import React, { useState, useEffect, useRef } from 'react'
import { Label } from '@res/styledComponents/index'
import { FaChevronDown } from 'react-icons/fa'

interface Props {
  rangeLabel: string
  onApply: (min?: number, max?: number) => void
}

const RangeDropdown: React.FC<Props> = ({ onApply, rangeLabel }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [minRange, setMinRange] = useState<number | undefined>(undefined)
  const [maxRange, setMaxRange] = useState<number | undefined>(undefined)
  const [selectedRange, setSelectedRange] = useState(
    `Select ${rangeLabel} range`,
  )
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleClear = () => {
    setMinRange(undefined)
    setMaxRange(undefined)
    setSelectedRange(`Select ${rangeLabel} range`)
  }

  const handleApply = () => {
    const hasMin = minRange !== undefined
    const hasMax = maxRange !== undefined

    if (hasMin || hasMax) {
      const min = hasMin ? minRange : '0'
      const max = hasMax ? maxRange : 'Unlimited'
      setSelectedRange(`${min} - ${max}`)
    } else {
      setSelectedRange(`Select ${rangeLabel} range`)
    }
    setIsOpen(false)
    if (onApply) {
      onApply(minRange, maxRange)
    }
  }

  return (
    <div className="w-full max-w-md relative" ref={dropdownRef}>
      <div className="mb-2">
        <Label>{rangeLabel}</Label>
      </div>

      {/* Dropdown Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-12 px-4 text-left font-normal border-2 border-gray-300 rounded-lg hover:border-gray-400 focus:border-gray-400 focus:outline-none bg-white flex items-center justify-between transition-colors"
      >
        <span
          className={
            selectedRange === `Select ${rangeLabel} range`
              ? 'text-gray-500'
              : 'text-gray-900'
          }
        >
          {selectedRange}
        </span>
        <FaChevronDown
          className={`h-4 w-4 text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-4">
                {rangeLabel} Range
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-start space-y-1">
                <label
                  htmlFor="min-range"
                  className="w-1/3 text-sm font-medium text-gray-700"
                >
                  Min {rangeLabel}
                </label>
                <input
                  id="min-range"
                  placeholder="Min"
                  type="number"
                  min="0"
                  value={minRange}
                  onChange={(e) =>
                    setMinRange(
                      e.target.value === ''
                        ? undefined
                        : Number(e.target.value),
                    )
                  }
                  className="h-10 border-2 border-solid border-gray-300 rounded-md focus:border-gray-400 focus:ring-0 w-full"
                />
              </div>

              <div className="flex flex-col items-start space-y-1">
                <label
                  htmlFor="max-range"
                  className="w-1/3 text-sm font-medium text-gray-700"
                >
                  Max {rangeLabel}
                </label>
                <input
                  id="max-range"
                  placeholder="Max"
                  type="number"
                  min="0"
                  value={maxRange}
                  onChange={(e) =>
                    setMaxRange(
                      e.target.value === ''
                        ? undefined
                        : Number(e.target.value),
                    )
                  }
                  className="h-10 border-2 border-solid border-gray-300 rounded-md focus:border-gray-400 focus:ring-0 w-full"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleClear}
                className="flex-1 h-10 border-2 border-solid border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md bg-transparent"
              >
                Clear
              </button>
              <button
                onClick={handleApply}
                className="flex-1 h-10 bg-black text-white hover:bg-gray-800 rounded-md"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RangeDropdown
